import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Provider,
  SimpleChanges
} from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Did } from "@onsip/common/services/api/resources/did/did";
import { DidService } from "@onsip/common/services/api/resources/did/did.service";
import { Subscription } from "rxjs";
import { switchMap, take, withLatestFrom } from "rxjs/operators";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { MatDialog } from "@angular/material/dialog";
import { AddNumberDialogComponent } from "@onsip/web/features/administrators/phoneNumbers/addPhoneNumberDialog/add-phone-number-dialog.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";

interface PhoneNumberItem {
  name: string;
  id: string;
  userId: string;
  assigned: boolean;
}

const VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => PhoneNumberListComponent),
  multi: true
};

@Component({
  selector: "onsip-phone-number-list",
  templateUrl: "./phone-number-list.component.html",
  styleUrls: ["./phone-number-list.component.scss"],
  providers: [VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhoneNumberListComponent
  implements OnInit, ControlValueAccessor, OnDestroy, OnChanges
{
  @Input() userName!: string | undefined;
  @Input() aor!: string;
  @Output() checkedNumbersEmitter: EventEmitter<Array<string>> = new EventEmitter();
  E2eLocators = E2eLocators;

  private unsubscriber = new Subscription();
  private didsValue: Array<Did> = [];
  private preSelectableNumbers!: Array<string>;

  phoneNumberList: Array<PhoneNumberItem> = [];
  controlValue = new FormControl();
  onTouched!: Function;

  constructor(
    private didService: DidService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  registerOnChange(onChanged: Function): void {
    this.unsubscriber.add(
      this.controlValue.valueChanges
        .pipe(withLatestFrom(this.didService.orgState))
        .subscribe(([value, dids]) => {
          onChanged(value);
          const phones = dids
            .filter(phone => this.controlValue.value.includes(phone.didId))
            .map(did => did.number);
          this.checkedNumbersEmitter.emit(phones);
        })
    );
  }

  registerOnTouched(fn: Function) {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    this.controlValue?.setValue(value);
  }

  getDidsData(didsData: Array<Did>): void {
    if (this.userName) {
      this.selectPredictedValue(didsData);
    }
    this.phoneNumberList = didsData.map(did => ({
      name: did.number,
      id: did.didId,
      userId: did.userId,
      assigned: !!did.userNumberAlias
    }));
    this.cdr.markForCheck();
  }

  async updatePhonesList(aor?: string, handleError = true): Promise<Array<ApiPromiseSubject<Did>>> {
    if (aor) this.aor = aor;
    this.aor = this.aor.toLowerCase();
    if (this.phoneNumberList?.length) {
      const phonesSelectedIds = this.controlValue?.value;
      const phonesList = this.phoneNumberList.map(phone => ({
        id: phone.id,
        userId: phone.userId
      }));
      const unassignPhones: Array<string> = this.preSelectableNumbers || [];

      return Promise.all([
        ...phonesList
          .filter(({ id }) => phonesSelectedIds.includes(id))
          .map(({ id, userId }) => {
            return this.didService.didAssign(id, this.aor, { UserId: userId }).then(response => {
              if (handleError) {
                this.showNotifyError(response.status, "assigning", "phone numbers");
              }
              return response;
            });
          }),
        ...unassignPhones
          .filter(el => !phonesSelectedIds.includes(el))
          .map(el => {
            return this.didService.didUnassign(el).then(response => {
              if (handleError) {
                this.showNotifyError(response.status, "unassigning", "phone numbers");
              }
              return response;
            });
          })
      ]);
    }

    return Promise.resolve([]);
  }

  private getPhoneNumberList(): void {
    this.didService.didBrowse();
    this.unsubscriber.add(
      this.didService.state
        .pipe(switchMap(() => this.didService.orgState.pipe(take(1))))
        .subscribe((did: Array<Did>) => {
          this.didsValue = did;
          this.getDidsData(did);
        })
    );
  }

  ngOnInit(): void {
    this.getPhoneNumberList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.userName?.currentValue) {
      this.getDidsData(this.didsValue);
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  private showNotifyError(status: string, actionType: string, entity: string): void {
    if (status === "error") {
      this.snackbarService.openSnackBar(`There is an error in ${actionType} ${entity}`, status);
    }
  }

  createAddNumberDialog(): void {
    this.dialog.open(AddNumberDialogComponent, {
      data: {
        disableToggles: true
      },
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      disableClose: true
    });
  }

  private selectPredictedValue(didsData: Array<Did>): void {
    if (this.controlValue?.value?.length) {
      this.preSelectableNumbers = this.controlValue.value;
    } else {
      this.preSelectableNumbers = didsData
        .filter(did => did?.userNumberAlias?.addressUsername === this.userName)
        .map(phone => phone.didId);
    }
    this.controlValue?.setValue(this.preSelectableNumbers);
  }
}
